import React, { useMemo } from "react";
import { Alert, Button, Modal, Skeleton, toast } from "@picklebet/ui";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { constructCurrency } from "common/currency";
import { walletService } from "@/features/wallet/services/wallet";
import { useDepositAmountValidationSchema } from "@/features/wallet/hooks/useDepositAmountValidationSchema";
import { ActiveGameSessionDepositInfo } from "@/features/wallet/components/ActiveGameSessionDepositInfo";
import { useDepositWizard } from "@/features/wallet/components/deposit/hooks/useDepositWizard";
import { useStashInteracReturnUrl } from "@/features/wallet/components/deposit/hooks/useInteracDepositReturn";
import {
  DepositContent,
  DepositContentSkeleton,
} from "../../blocks/deposit-content/DepositContent";
import { useReportSubmitting } from "@/features/wallet/hooks/useReportSubmitting";
import { DepositAmountInput } from "../../blocks/deposit-amount-input/DepositAmountInput";
import { DepositMethodField } from "../../blocks/deposit-method-field/DepositMethodField";
import { SectionHeader } from "../../blocks/section-header/SectionHeader";
import { AmountInputSkeleton } from "@/features/wallet/components/shared/amount-input-skeleton/AmountInputSkeleton";
import { SectionDivider } from "../../blocks/section-divider/SectionDivider";
import { MethodRowSkeleton } from "../../method-row/MethodRow";
import { useShellStore } from "@/features/shell/stores/shellStore";
import { usePermission } from "@/features/auth/hooks/usePermission";
import { PROFILE_COMPLETION_MODAL } from "@/features/auth/components/ProfileCompletionModal";

import * as styles from "./InteracPrimaryView.module.scss";

const MIN_DEPOSIT_AMOUNT = 10;
const MAX_DEPOSIT_AMOUNT = 10_000;
const MAX_DEPOSIT_AMOUNT_CENTS = MAX_DEPOSIT_AMOUNT * 100;

export const InteracPrimaryViewSkeleton = () => {
  return (
    <DepositContentSkeleton>
      <AmountInputSkeleton />

      <SectionDivider />

      <Skeleton
        size="xs"
        span="half"
        className={styles.sectionHeaderSkeleton}
      />

      <MethodRowSkeleton />

      <Skeleton className={styles.buttonSkeleton} />
    </DepositContentSkeleton>
  );
};

export const InteracPrimaryView = () => {
  const { amount, setAmount, warnBetslipLoss, setSubmitting } =
    useDepositWizard();
  const stashReturnUrl = useStashInteracReturnUrl();
  const [amountSchema, loading] = useDepositAmountValidationSchema();
  const setModal = useShellStore((state) => state.setModal);
  const incompleteProfile = usePermission("deposit") === "INCOMPLETE_PROFILE";

  const schema = useMemo(
    () =>
      amountSchema.superRefine((values, ctx) => {
        if (values.amount < MIN_DEPOSIT_AMOUNT) {
          ctx.addIssue({
            code: "custom",
            path: ["amount"],
            message: `Amount must be ${constructCurrency(
              MIN_DEPOSIT_AMOUNT * 100,
              { currencyType: "CAD" },
            )} or more`,
          });
        }

        if (values.amount > MAX_DEPOSIT_AMOUNT) {
          ctx.addIssue({
            code: "custom",
            path: ["amount"],
            message: `Amount must be ${constructCurrency(
              MAX_DEPOSIT_AMOUNT_CENTS,
              { currencyType: "CAD" },
            )}, or less, per transaction`,
          });
        }
      }),
    [amountSchema],
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { amount },
  });

  useReportSubmitting(isSubmitting, setSubmitting);

  const onSubmit = async (values: { amount: number }) => {
    if (incompleteProfile) {
      setModal(PROFILE_COMPLETION_MODAL);
      return;
    }

    const result = await walletService.interacDeposit(values.amount);

    if (!result.ok) {
      toast({
        variant: "danger",
        title: "Deposit Failed",
        description: result.error.message,
      });
      return;
    }

    const form = document.createElement("form");
    form.method = "POST";
    form.action = result.data.redirectUrl;

    const input = document.createElement("input");
    input.type = "hidden";
    input.name = "token"; // field name expected by the endpoint
    input.value = result.data.paymentToken;
    form.appendChild(input);

    document.body.appendChild(form);

    stashReturnUrl();
    form.submit();
  };

  if (loading) {
    return <InteracPrimaryViewSkeleton />;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DepositContent>
        <ActiveGameSessionDepositInfo
          className={styles.activeGameSession}
          hasCTA={false}
        />

        {warnBetslipLoss && (
          <Alert color="warning" size="small">
            You will be redirected to Interac to complete your deposit. Your
            Betslip selections will be cleared.
          </Alert>
        )}

        <Controller
          control={control}
          name="amount"
          render={({ field: { onChange, value } }) => (
            <DepositAmountInput
              value={value}
              onChange={(next) => {
                onChange(next);
                setAmount(next);
              }}
              errorMessage={errors.amount?.message}
              disabled={isSubmitting}
              maxAmountOverrideCents={MAX_DEPOSIT_AMOUNT_CENTS}
            />
          )}
        />

        <SectionDivider />

        <SectionHeader>Payment Method</SectionHeader>

        <DepositMethodField />

        <Modal.Footer>
          <Button
            className={styles.depositButton}
            type="submit"
            disabled={amount === 0 || isSubmitting}
            loading={isSubmitting}
          >
            Deposit {constructCurrency(amount * 100, { currencyType: "CAD" })}
          </Button>
        </Modal.Footer>
      </DepositContent>
    </form>
  );
};
